import axios from 'axios';
import toast from 'react-hot-toast';

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 60000,  // 60s — enough for image uploads
  headers: {},
});

// ── Request interceptor —  ────────────────
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor — ──
let refreshing = false;
let queue = [];

const processQueue = (error, token = null) => {
  queue.forEach(({ resolve, reject }) => error ? reject(error) : resolve(token));
  queue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const authEndpoints = [
      '/auth/login',
      '/auth/register',
      '/auth/send-otp',
      '/auth/verify-otp',
      '/auth/forgot',
      '/auth/reset',
      '/auth/logout',
      '/auth/logout-all',
    ];

    const isAuthEndpoint = authEndpoints.some((endpoint) => original.url?.includes(endpoint));

    if (error.response?.status === 401 && !original._retry && !isAuthEndpoint) {
      if (refreshing) {
        return new Promise((resolve, reject) => {
          queue.push({ resolve, reject });
        }).then((token) => {
          original.headers.Authorization = `Bearer ${token}`;
          return api(original);
        });
      }

      original._retry = true;
      refreshing = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) throw new Error('No refresh token');

        const { data } = await axios.post(`${BASE_URL}/auth/refresh-token`, { refreshToken });
        const newToken = data.data.accessToken;
        localStorage.setItem('accessToken', newToken);
        localStorage.setItem('refreshToken', data.data.refreshToken);
        processQueue(null, newToken);
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch (err) {
        processQueue(err, null);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(err);
      } finally {
        refreshing = false;
      }
    }

    if (!error.response) {
      // Skip toast for cancelled/aborted requests
      if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
        return Promise.reject(error);
      }
      // Only show toast once per second to avoid spam
      const now = Date.now();
      if (!window._lastNetworkErrorToast || now - window._lastNetworkErrorToast > 3000) {
        window._lastNetworkErrorToast = now;
        const url = error.config?.url || '';
        toast.error(
          error.code === 'ECONNABORTED'
            ? 'Request timed out. Please try again.'
            : `Network Error: Could not reach server. (${url})`
        );
      }
      return Promise.reject(error);
    }

    const msg = error.response?.data?.message || error.response?.data?.msg || error.message || 'Something went wrong';
    if (error.response?.status === 401 && isAuthEndpoint) {
      toast.error(msg);
      return Promise.reject(error);
    }

    if (error.response?.status !== 401) toast.error(msg);
    return Promise.reject(error);
  }
);

// ── Auth ──────────────────────────────────────────────
export const authAPI = {
  loginEmail: (d) => api.post('/auth/login', d),
  registerEmail: (d) => api.post('/auth/register', d),
  socialLogin: (d) => api.post('/auth/social-login', d),
  sendOtp: (d) => api.post('/auth/send-otp', d),
  verifyOtp: (d) => api.post('/auth/verify-otp', d),
  logout: (d) => api.post('/auth/logout', d),
  logoutAll: () => api.post('/auth/logout-all'),
  forgotPassword: (d) => api.post('/auth/forgot', d),
  resetPassword: (d) => api.post('/auth/reset', d),
  socialLogin: (d) => api.post('/auth/social-login', d),
  getSessions: () => api.get('/auth/sessions'),
  revokeSession: (id) => api.delete(`/auth/sessions/${id}`),
  blockTokens: (d) => api.post('/auth/admin/block-tokens', d),
};

// ── Users ─────────────────────────────────────────────
export const usersAPI = {
  list: (p) => api.get('/admin/users', { params: p }),
  get: (id) => api.get(`/admin/users/${id}`),
  update: (id, d) => api.patch(`/admin/users/${id}`, d),
  suspend: (d) => api.post('/admin/users/suspend', d),
  unsuspend: (id) => api.patch(`/admin/users/${id}/unsuspend`),
};


export const inquiriesAPI = {
  grouped: () => api.get('/products/inquiry/grouped'),
  getByUser: () => api.get('/products/inquiry/me'),
  updateStatus: (id, status) => api.patch(`/products/inquiry/${id}/status`, { status }),
};
// ── Products ──────────────────────────────────────────
export const productsAPI = {
  brands: (p) => api.get('/products/brands', { params: p }),
  list: (p) => api.get('/products', { params: p }),
  get: (slug) => api.get(`/products/${slug}`),
  create: (d) => api.post('/products/create', d),
  createV2: (d) => api.post('/products/create', d),

  update: (id, d) => api.put(`/products/${id}`, d),

  delete: (id) => api.delete(`/products/${id}`),
  bulkUpload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/products/bulk-upload', formData);
  },
  jobStatus: (jid) => api.get(`/products/bulk-upload/job/${jid}`),
  updateInventory: (d) => api.put('/products/inventory/update', d),
  reviews: (pid, p) => api.get(`/products/${pid}/reviews`, { params: p }),
  lowStockAlerts: () => api.get('/products/low-stock/alerts'),
};

// ── Categories ────────────────────────────────────────
export const categoriesAPI = {
  list: (p) => api.get('/categories', { params: p }),
  tree: () => api.get('/categories/tree'),
  adminList: (p) => api.get('/categories/admin/all', { params: p }),
  create: (d) => api.post('/categories', d),
  update: (id, d) => api.put(`/categories/${id}`, d),
  toggle: (id, d) => api.patch(`/categories/${id}/toggle`, d),
  delete: (id) => api.delete(`/categories/${id}`),
};




// ── Orders ────────────────────────────────────────────
export const ordersAPI = {
  place: (d) => api.post('/orders', d),
  myOrders: (p) => api.get('/orders/my-orders', { params: p }),
  detail: (id) => api.get(`/orders/${id}`),
  cancel: (id, d) => api.patch(`/orders/${id}/cancel`, d),
  adminList: (p) => api.get('/orders/admin/all', { params: p }),
  updateStatus: (id, d) => api.patch(`/orders/${id}/status`, d),
  updateItemStatus: (orderId, itemId, d) => api.patch(`/orders/${orderId}/items/${itemId}/status`, d),
};

// ── Payments ──────────────────────────────────────────
export const paymentsAPI = {
  createOrder: (d) => api.post('/payments/create-order', d),
  verify: (d) => api.post('/payments/verify', d),
  history: (p) => api.get('/payments/history', { params: p }),
  refund: (d) => api.post('/payments/refund', d),
  stats: (p) => api.get('/payments/stats', { params: p }),
};

// ── Businesses ────────────────────────────────────────
export const businessAPI = {
  create: (d) => api.post('/businesses', d),
  mine: () => api.get('/businesses/mine'),
  update: (id, d) => api.put(`/businesses/${id}`, d),
  adminList: (p) => api.get('/admin/businesses', { params: p }),
  approve: (id) => api.patch(`/admin/businesses/${id}/approve`),
  suspend: (id, d) => api.patch(`/admin/businesses/${id}/suspend`, d),
  reject: (id, d) => api.patch(`/admin/businesses/${id}/reject`, d),
  commission: (id, d) => api.patch(`/admin/businesses/${id}/commission`, d),
};

// ── Returns ───────────────────────────────────────────
export const returnsAPI = {
  create: (d) => api.post('/returns', d),
  my: () => api.get('/returns/my'),
  mineReturn: () => api.get('/returns/mine'),
  adminList: (p) => api.get('/returns', { params: p }),
  update: (id, d) => api.put(`/returns/${id}`, d),
  detail: (id) => api.get(`/returns/${id}`),
  updateStatus: (id, d) => api.patch(`/returns/${id}/status`, d),
};

// ── Cart ──────────────────────────────────────────────
export const cartAPI = {
  get: () => api.get('/cart'),
  add: (d) => api.post('/cart/items', d),
  update: (id, d) => api.put(`/cart/items/${id}`, d),
  remove: (id) => api.delete(`/cart/items/${id}`),
  clear: () => api.delete('/cart'),
};

// ── Wishlist ──────────────────────────────────────────
export const wishlistAPI = {
  get: () => api.get('/wishlist'),
  add: (d) => api.post('/wishlist/items', d),
  check: (pid) => api.get(`/wishlist/items/${pid}`),
  remove: (pid) => api.delete(`/wishlist/items/${pid}`),
  clear: () => api.delete('/wishlist'),
};

// ── Chat ──────────────────────────────────────────────
export const chatAPI = {
  createRoom: (d) => api.post('/chat', d),
  myRooms: (p) => api.get('/chat/my', { params: p }).catch(() => ({ data: { rows: [], count: 0 } })),
  messages: (rid, p) => api.get(`/chat/${rid}/messages`, { params: p }),
  sendMessage: (rid, d) => api.post(`/chat/${rid}/messages`, d),
  adminRooms: (p) => api.get('/chat/admin/all', { params: p }).catch(() => ({ data: { rows: [], count: 0 } })),
  assign: (rid, d) => api.put(`/chat/${rid}/assign`, d),
  close: (rid) => api.put(`/chat/${rid}/close`),
};

// ── Admin ─────────────────────────────────────────────
export const adminAPI = {
  // Dashboard & Metrics
  dashboard: (p) => api.get('/admin/dashboard', { params: p }),

  // Sellers & KYC Governance
  sellersList: (p) => api.get('/admin/businesses', { params: p }),
  sellerDetail: (id) => api.get(`/admin/businesses/${id}`),
  approveSeller: (id, config = {}) => api.patch(`/admin/businesses/${id}/approve`, {}, config),
  rejectSeller: (id, d, config = {}) => api.patch(`/admin/businesses/${id}/reject`, d, config),
  suspendSeller: (id, d, config = {}) => api.patch(`/admin/businesses/${id}/suspend`, d, config),
  overrideCommission: (id, d, config = {}) => api.patch(`/admin/businesses/${id}/commission`, d, config),
  getKycDocToken: (id, docType) => api.post(`/admin/businesses/${id}/documents/${docType}/access-token`),

  // Product Moderation
  moderationList: (p) => api.get('/admin/products/moderation', { params: p }),
  allProducts: (p) => api.get('/products', { params: p }),
  approveProduct: (id, config = {}) => api.patch(`/admin/products/${id}/approve`, {}, config),
  rejectProduct: (id, d, config = {}) => api.patch(`/admin/products/${id}/reject`, d, config),
  suspendProduct: (id, d, config = {}) => api.patch(`/admin/products/${id}/suspend`, d, config),
  toggleFeaturedProduct: (id, d) => api.patch(`/admin/products/${id}/featured`, d),

  // Master Orders & Disputes
  forceCancelOrder: (id, d, config = {}) => api.post(`/admin/orders/${id}/force-cancel`, d, config),
  disputeResolve: (id, d, config = {}) => api.post(`/admin/orders/${id}/dispute-resolve`, d, config),

  // Inventory & Warehouse Governance
  inventoryOverview: (p) => api.get('/admin/inventory/overview', { params: p }).catch(() => api.get('/products', { params: p })).catch(() => ({ data: { rows: [], count: 0 } })),
  inventoryAdjust: (d, config = {}) => api.post('/admin/inventory/adjust', d, config),
  inventoryHistory: (p) => api.get('/admin/inventory/history', { params: p }).catch(() => ({ data: { rows: [], count: 0 } })),

  // Double-Entry Finance & Payouts
  ledgerTransactions: (p) => api.get('/admin/finance/transactions', { params: p }),
  payouts: (p) => api.get('/admin/payouts', { params: p }),
  createPayoutBatch: (d, config = {}) => api.post('/admin/finance/payout-batch', d, config),
  processRefund: (d, config = {}) => api.post('/admin/finance/refunds', d, config),
  invoice: (oid) => api.get(`/admin/invoices/${oid}`),

  // Customers Governance
  customersList: (p) => api.get('/admin/users', { params: p }),
  customerDetail: (id) => api.get(`/admin/users/${id}`),
  blockCustomer: (id, d, config = {}) => api.post(`/admin/users/${id}/block`, d, config),
  unblockCustomer: (id, d, config = {}) => api.post(`/admin/users/${id}/unblock`, d, config),

  // Staff & RBAC
  staffList: (p) => api.get('/admin/staff', { params: p }),
  createStaff: (d) => api.post('/admin/staff', d),
  rolesList: (p) => api.get('/admin/roles', { params: p }),
  updateRolePermissions: (roleId, d, config = {}) => api.put(`/admin/roles/${roleId}/permissions`, d, config),
  activeSessions: (p) => api.get('/admin/sessions', { params: p }),
  revokeSession: (sessionId, config = {}) => api.delete(`/admin/sessions/${sessionId}`, config),
  blockedTokens: (p) => api.get('/admin/blocked-tokens', { params: p }),

  // Security Center & Audit Trail
  securityEvents: (p) => api.get('/admin/security/events', { params: p }),
  failedLogins: (p) => api.get('/admin/security/failed-logins', { params: p }),
  auditLogs: (p) => api.get('/admin/audit-logs', { params: p }),

  // System Configuration
  getSettings: (p) => api.get('/admin/settings', { params: p }).catch(() => ({ data: { settings: [], data: [] } })),
  updateSetting: (key, d, config = {}) => api.put(`/admin/settings/${key}`, d, config),

  // Marketing & Coupons
  couponsList: (p) => api.get('/coupon/list', { params: p }),
  createCoupon: (d) => api.post('/coupon/add', d),
  updateCoupon: (id, d) => api.put(`/coupon/${id}`, d),
  deleteCoupon: (id) => api.delete(`/coupon/${id}`),

  // Reviews Moderation
  moderateReview: (id, d) => api.patch(`/admin/reviews/${id}/moderate`, d),
  verifyReview: (id, config = {}) => api.patch(`/admin/reviews/${id}/verify`, {}, config),
  deleteReview: (id) => api.delete(`/admin/reviews/${id}`),
};

// ── Reviews (Business) ────────────────────────────────
export const reviewsAPI = {
  // Get all reviews for the business's products (pending + verified)
  businessList: (p) => api.get('/products/reviews/business', { params: p }),
  // Verify or reject a review
  verify: (reviewId, d) => api.patch(`/products/reviews/${reviewId}/verify`, d),
};


// ── Notifications ─────────────────────────────────────
export const notifAPI = {
  list: (p) => api.get('/notifications', { params: p }),
  unreadCount: () => api.get('/notifications/unread-count'),
  markOne: (id) => api.patch(`/notifications/${id}/read`),
  markMany: (d) => api.post('/notifications/read', d),
  markAll: () => api.patch('/notifications/read-all'),
};

export const bannersAPI = {
  list: (p) => api.get('/banners', { params: p }),
  adminGetAll: (p) => api.get('/banners/admin/all', { params: p }),
  create: (d) => api.post('/banners', d),
  update: (id, d) => api.put(`/banners/${id}`, d),
  toggleActive: (id, d) => api.patch(`/banners/${id}/toggle`, d),
  delete: (id) => api.delete(`/banners/${id}`),
};

export default api;
